import * as React from 'react'
import styled from 'styled-components'
import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next'
import { captureException } from '@sentry/nextjs'
import dynamic from 'next/dynamic'

import { ProfileMetadata } from '../interfaces'
import { fbGetUserMetadata } from '../data/FirebaseEnrich'
import { getShortenedAddress } from '../utils/address'

const Layout = dynamic(() => import('../components/global').then((mod) => mod.Layout))
const ProfileTabs = dynamic(() => import('../components/tabs/ProfileTabs'))
const ProfileHeader = dynamic(() =>
  import('../components/profile').then((mod) => mod.ProfileHeader)
)
const AmplitudeInit = dynamic(() => import('../components/global/AmplitudeInit'), {
  ssr: false,
})

// ========================== Styled components ================================

const Column = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  position: relative;
  /* padding-left: 45px;
  padding-right: 45px; */
  max-width: 1320px;
  height: 100%;
  /* overflow-y: auto; */

  /* @media (max-width: 43em) {
    padding-left: 25px;
    padding-right: 25px;
  } */
`

const NoProfileFound = styled.div`
  margin-left: 10px;
  margin-right: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 85vh;
  font-size: 30px;
  font-weight: 700;
  text-align: center;
`

// ================================= Types =====================================

type ProfilePageProps = {
  profileExists?: boolean
  address: string
  metadata?: ProfileMetadata
  hasCreated?: boolean
}

// ========================== React component ==================================

const ProfilePage = ({ profileExists, address, metadata }: ProfilePageProps): JSX.Element => {
  if (!address) {
    return <></>
  }

  if (!profileExists) {
    return (
      <Layout title="No profile found.">
        <NoProfileFound>{`This profile doesn't exist.`}</NoProfileFound>
      </Layout>
    )
  }

  // solana address validation

  return (
    <Layout title={`${metadata?.display_name ?? getShortenedAddress(address)} | Profile`}>
      <AmplitudeInit page="Profile" />
      <ProfileHeader address={address} metadata={metadata} />
      <Column>
        <ProfileTabs hasListings={false} address={address} />
      </Column>
    </Layout>
  )
}

export default ProfilePage

export const getServerSideProps = async (
  context: GetServerSidePropsContext<{ address: string }>
): Promise<
  GetServerSidePropsResult<{
    address: string
    profileExists: boolean
    metadata: ProfileMetadata | undefined
  }>
> => {
  context.res.setHeader('Cache-Control', 'public, s-maxage=10, stale-while-revalidate=21600')
  const { address = '' } = context.params || {}

  let exists = false
  try {
    const { PublicKey } = await import('@solana/web3.js')
    const publicKey = new PublicKey(address)
    exists = PublicKey.isOnCurve(publicKey.toBuffer())
  } catch (e) {
    console.log(e)
    exists = false
    captureException(e)
  }

  const metadata: ProfileMetadata | undefined = await fbGetUserMetadata(address)

  return {
    props: {
      address,
      profileExists: exists,
      metadata,
    },
  }
}
